import { ENDPOINTS } from '@/util'
import { Edit24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { ErrorComponent } from '../ErrorComponent'
import { EditApplicationAttribsForm } from '../Forms/ApplicationAttribsForm'
import { Loading } from '../Loading'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import './AppMarketingPanel.scss'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsApplicationAttributes = RestSchemas['Messages.Applications.ApplicationAttributes']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export const AppMarketingPanel = () => {
  const appId = useGetSearchParam<number>('appId')

  const { getApplicationsApiGetApplications, getApplicationsApiGetApplicationAttributes } = createRestClient()

  const {
    data: application,
    isPending: isApplicationPending,
    isError: isApplicationError,
  } = getApplicationsApiGetApplications.useQuery(undefined, {
    select: (data) => data.find((x) => x.applicationId === appId),
  })

  const {
    data: applicationAtt,
    isPending: isApplicationAttPending,
    isError: isApplicationAttError,
  } = getApplicationsApiGetApplicationAttributes.useQuery(
    {
      query: { applicationId: appId },
    },
    { enabled: !!appId }
  )

  if (isApplicationPending || isApplicationAttPending) return <Loading />
  if (isApplicationError || isApplicationAttError || !application) return <ErrorComponent />
  return <PageContent {...{ application, applicationAtt }} />
}

function PageContent({
  application,
  applicationAtt,
}: {
  application: MessagesApplicationsApplication
  applicationAtt: MessagesApplicationsApplicationAttributes
}) {
  const [editAppAttrOpen, setEditAppAttrOpen] = useState(false)
  const imagePrefix = `${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=`

  const { appName } = application

  const {
    differentiatedBenefit1Pictogram,
    differentiatedBenefit1,
    differentiatedBenefit2Pictogram,
    differentiatedBenefit2,
    differentiatedBenefit3Pictogram,
    differentiatedBenefit3,
    imageKey,
    problemStatement,
    keyQuestions,
    solutionDescription,
    keyContactId1,
    keyContactId2,
    keyContactId3,
  } = applicationAtt

  const { getSubscriptionsApiGetOrganisationUser } = createRestClient()

  // Queries
  const { data: keyContact1 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId1 ?? undefined },
    },
    { enabled: !!keyContactId1 }
  )

  const { data: keyContact2 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId2 ?? undefined },
    },
    { enabled: !!keyContactId2 }
  )

  const { data: keyContact3 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId3 ?? undefined },
    },
    { enabled: !!keyContactId3 }
  )

  // Data Arrays to render
  const keyContacts = [keyContact1, keyContact2, keyContact3].filter(Boolean)

  const differentiatedBenefits = [
    { pictogram: differentiatedBenefit1Pictogram, text: differentiatedBenefit1 },
    { pictogram: differentiatedBenefit2Pictogram, text: differentiatedBenefit2 },
    { pictogram: differentiatedBenefit3Pictogram, text: differentiatedBenefit3 },
  ].filter(({ text, pictogram }) => !!text || !!pictogram)

  return (
    // <ErrorBoundary>
    <div className="application-cards">
      <div className="application-card">
        <div className="application-content">
          <div className="header-layout">
            <div className="section-heading">{appName}</div>

            {imageKey && (
              <div
                className="key-image"
                style={{
                  backgroundImage: `url("${imagePrefix}${imageKey}")`,
                }}
              />
            )}
          </div>

          <div className="section-heading">{problemStatement}</div>
          <div className="section-text">{keyQuestions}</div>
          <div className="section-heading">How this app tackles the problem statement</div>
          <hr />
          <div className="section-text">{solutionDescription}</div>

          <br />
          <div className="section-heading">Benefits that differentiate this app</div>
          <hr />
          <div className="benefits">
            {differentiatedBenefits.map(({ text, pictogram }) => (
              <div key={`${text}-${pictogram}`} className="benefit">
                {pictogram && (
                  <div
                    className="benefit-image"
                    style={{
                      backgroundImage: `url("${imagePrefix}${pictogram}")`,
                    }}
                  />
                )}
                <div className="benefit-description">{text}</div>
              </div>
            ))}
          </div>
          <br />
          <div className="section-heading">Get in touch with our app leads</div>
          <hr />
          <div className="keyContacts">
            {keyContacts.map((keyContact) => (
              <KeyContact key={keyContact?.id} keyContact={keyContact!} />
            ))}
          </div>
        </div>
        <div>
          <Edit24Regular onClick={() => setEditAppAttrOpen(true)} />
        </div>
        <OverlayDrawerComponent
          open={editAppAttrOpen}
          setOpen={setEditAppAttrOpen}
          title="Edit application details"
          size="medium"
          content={
            <EditApplicationAttribsForm
              onSubmit={() => {
                setEditAppAttrOpen(false)
              }}
              selectedApp={application}
            />
          }
        />
      </div>
    </div>
  )
}

function KeyContact({ keyContact }: { keyContact: MessagesSubscriptionsOrganisationUser }) {
  const imagePrefix = `${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=`
  return (
    <div className="keyContact">
      {keyContact.profilePicture && (
        <div
          className="keyContact-image"
          style={{ backgroundImage: `url("${imagePrefix}${keyContact.profilePicture}")` }}
        />
      )}
      <div className="keyContactName">
        {keyContact.givenName}
        &nbsp;
        {keyContact.familyName}
      </div>
      <div className="keyContactEmail">{keyContact.email}</div>
    </div>
  )
}
